// Configuration for email blacklist
// Users in this list will be denied access to specific paths/features
// This allows fine-grained control over user access on a per-route basis

export const EMAIL_BLACKLIST_CONFIG = {
  // Case-sensitive email matching (default: false)
  caseSensitive: false,
  
  // Whether to trim whitespace from emails before comparison (default: true)
  trimWhitespace: true,
  
  // Default redirect path for blacklisted users
  unauthorizedRedirectPath: '/auth/unauthorized',
  
  // Default redirect path for login
  loginRedirectPath: '/auth/login',
  
  // Path-based blacklist mapping
  // Format: { '/path': ['email1@domain.com', 'email2@domain.com'] }
  // Supports wildcards: '/venues/*' will match /venues/123, /venues/456, etc.
  // Example patterns:
  // '/admin/reports': ['restricted-user@example.com'],  // Exact path
  // '/venues/*': ['venue-restricted@example.com'],      // All venues and sub-paths
  // '/admin/*': ['admin-user@example.com'],             // All admin paths
  // '/*/settings': ['settings-restricted@example.com']  // All settings paths
  // '/events': ['user@example.com'],                    // Exact /events path
  pathBlacklists: {
    '/dashboard': ['atiba@fitterandfaster.com'],
    '/dashboard/*': ['atiba@fitterandfaster.com'],
    '/events': ['atiba@fitterandfaster.com'],
    '/events/*': ['atiba@fitterandfaster.com'],
    '/sessions': ['atiba@fitterandfaster.com'],
    '/sessions/*': ['atiba@fitterandfaster.com'],
    '/swim-team': ['atiba@fitterandfaster.com'],
    '/swim-team/*': ['atiba@fitterandfaster.com'],
    '/customers': ['atiba@fitterandfaster.com'],
    '/customers/*': ['atiba@fitterandfaster.com'],
    '/orders': ['atiba@fitterandfaster.com'],
    '/orders/*': ['atiba@fitterandfaster.com'],
    '/transfers': ['atiba@fitterandfaster.com'],
    '/transfers/*': ['atiba@fitterandfaster.com'],
    '/waitlists': ['atiba@fitterandfaster.com'],
    '/waitlists/*': ['atiba@fitterandfaster.com'],
    '/gear-sales': ['atiba@fitterandfaster.com'],
    '/gear-sales/*': ['atiba@fitterandfaster.com'],
    '/email-templates': ['atiba@fitterandfaster.com'],
    '/email-templates/*': ['atiba@fitterandfaster.com'],
    '/scheduled': ['atiba@fitterandfaster.com'],
    '/scheduled/*': ['atiba@fitterandfaster.com']
    // '/admin/reports': ['restricted-user@example.com'],
    // '/venues/*': ['venue-restricted@example.com'],
    // '/finance/*': ['finance-restricted@example.com'],
  }
};

// Global blacklist for users who should not access the application at all
export const GLOBAL_EMAIL_BLACKLIST = [
  // Add email addresses here that should be completely denied access
  // 'blocked-user@example.com'
];

/**
 * Match a path against a pattern that may include wildcards (*)
 * @param path - The actual path being accessed (e.g., '/venues/123' or '/events/event/621087')
 * @param pattern - The pattern to match against (e.g., '/venues/*' or '/events/*')
 * @returns boolean - True if path matches pattern, false otherwise
 */
export function matchPathPattern(path: string, pattern: string): boolean {
  // Exact match
  if (path === pattern) {
    return true;
  }
  
  // Convert wildcard pattern to regex
  // Replace * with a regex that matches any characters (including multiple path segments)
  // This allows /events/* to match /events/event/621087
  const regexPattern = pattern.replace(/\*/g, '.*');
  const regex = new RegExp(`^${regexPattern}$`);
  
  return regex.test(path);
}

/**
 * Get the blacklist for a specific path
 * Supports both exact matches and wildcard patterns
 * @param path - The route path to get the blacklist for (e.g., '/venues/123')
 * @param isGlobal - Whether to include global blacklist
 * @returns string[] - Array of blacklisted email addresses for this path
 */
export function getPathBlacklist(path: string, isGlobal: boolean = true): string[] {
  const matchedBlacklists = new Set<string>();
  
  // Check all path patterns in the config
  for (const pattern in EMAIL_BLACKLIST_CONFIG.pathBlacklists) {
    if (matchPathPattern(path, pattern)) {
      EMAIL_BLACKLIST_CONFIG.pathBlacklists[pattern].forEach(email => {
        matchedBlacklists.add(email);
      });
    }
  }
  
  const result = Array.from(matchedBlacklists);
  
  if (isGlobal) {
    return [...new Set([...GLOBAL_EMAIL_BLACKLIST, ...result])]; // Remove duplicates using Set
  }
  
  return result;
}
